import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AuftragsAkteResponse, AuftragsAkteRechnung, AuftragKalkulation } from '../types';
import { useAktiveModule } from '../hooks/useAktiveModule';
import { downloadDateiOderFehler } from '../lib/download';
import BelegAnsichtModal from './BelegAnsichtModal';
import Timeline from './Timeline';
import AnhangPanel from './AnhangPanel';
import NotizenAufgaben from './NotizenAufgaben';
import DokumenteKarte from './DokumenteKarte';
import RegieberichtForm, { RegieberichtKarte, leseRolleAusSpeicher } from './RegieberichtForm';
import './AuftragsAkte.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface AuftragsAkteProps {
  auftragId: number;
  onBack: () => void;
}

const TYP_LABEL: Record<string, string> = {
  PRODUKTION: 'Produktion',
  DIENSTLEISTUNG: 'Dienstleistung / Handwerk',
  HANDEL: 'Handel',
  PROJEKT: 'Projektgeschäft',
};

const ABSCHLUSS_STATI = ['Geliefert', 'Fakturiert'];

function statusKind(status: string | null | undefined): 'offen' | 'fortschritt' | 'abschluss' | 'storniert' {
  if (status === 'Storniert') return 'storniert';
  if (status && ABSCHLUSS_STATI.includes(status)) return 'abschluss';
  if (status === 'Angebot') return 'offen';
  return 'fortschritt';
}

function einsatzStatusKind(status: string | null | undefined): 'offen' | 'fortschritt' | 'abschluss' | 'storniert' {
  if (status === 'STORNIERT') return 'storniert';
  if (status === 'ABGESCHLOSSEN') return 'abschluss';
  if (status === 'GEPLANT') return 'offen';
  return 'fortschritt';
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('de-DE');
}

function formatPrice(price?: number | null): string {
  if (price === null || price === undefined) return '-';
  return `${price.toFixed(2)} €`;
}

function prioClass(p: number): string {
  return p >= 7 ? 'high' : p >= 4 ? 'medium' : 'low';
}

/** Ampel für den Deckungsbeitrag: 'over' (Ist > Soll), 'warn' (Marge < 10%
 * vom Soll), sonst 'ok'. Bei soll_gesamt <= 0 zählt nur, ob überschritten. */
function deckungsbeitragAmpel(deckungsbeitrag: number, sollGesamt: number): 'ok' | 'warn' | 'over' {
  if (deckungsbeitrag < 0) return 'over';
  if (sollGesamt > 0 && deckungsbeitrag / sollGesamt < 0.1) return 'warn';
  return 'ok';
}

const nr4 = (id: number) => String(id).padStart(4, '0');

type BelegAnsichtState = { url: string; title: string } | null;

const AuftragsAkte: React.FC<AuftragsAkteProps> = ({ auftragId, onBack }) => {
  const [akte, setAkte] = useState<AuftragsAkteResponse | null>(null);
  const [statusOptionen, setStatusOptionen] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusAktion, setStatusAktion] = useState<string | null>(null);
  const [statusFehler, setStatusFehler] = useState<string | null>(null);

  const [rechnungLaedt, setRechnungLaedt] = useState(false);
  const [rechnungFehler, setRechnungFehler] = useState<string | null>(null);

  const [downloadFehler, setDownloadFehler] = useState('');
  const [belegAnsicht, setBelegAnsicht] = useState<BelegAnsichtState>(null);

  const [kalkulation, setKalkulation] = useState<AuftragKalkulation | null>(null);
  const [materialDetailOffen, setMaterialDetailOffen] = useState(false);
  const [zeitDetailOffen, setZeitDetailOffen] = useState(false);
  const [quelleBerichtOffen, setQuelleBerichtOffen] = useState(false);

  const aktiveModule = useAktiveModule();
  const authToken = localStorage.getItem('auth_token') || '';
  // Die Akte bekommt (anders als ManagementPage) keine Props von oben — die
  // Rolle steht im selben localStorage-Eintrag, den App.tsx beim Login setzt.
  // Ohne sie blendete die RegieberichtKarte ihre Abrechnungs-Aktionen auch
  // für Berechtigte aus (darfAbrechnen ist fail-closed).
  const userRolle = leseRolleAusSpeicher();

  const modulAktiv = useCallback(
    (modul: string) => aktiveModule.size === 0 || aktiveModule.has(modul),
    [aktiveModule]
  );

  const ladeAkte = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<AuftragsAkteResponse>(`${API_URL}/api/auftraege/${auftragId}/akte`);
      setAkte(res.data);

      const typ = res.data.auftrag.auftrag_typ;
      if (typ) {
        try {
          const so = await axios.get(`${API_URL}/api/auftraege/status-optionen?auftrag_typ=${typ}`);
          setStatusOptionen(so.data.status || []);
        } catch {
          setStatusOptionen([]);
        }
      }

      try {
        const kalk = await axios.get<AuftragKalkulation>(`${API_URL}/api/auftraege/${auftragId}/kalkulation`);
        setKalkulation(kalk.data);
      } catch {
        // Nachkalkulation ist eine Zusatz-Kachel — ein Fehler dort blockiert
        // nicht die restliche Auftragsakte.
        setKalkulation(null);
      }
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data?.detail ?? err.message)
        : 'Unbekannter Fehler';
      setError(`Auftragsakte konnte nicht geladen werden: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [auftragId]);

  useEffect(() => {
    ladeAkte();
  }, [ladeAkte]);

  const handleStatusChange = async (zielStatus: string) => {
    setStatusFehler(null);
    setStatusAktion(zielStatus);
    try {
      await axios.put(`${API_URL}/api/auftraege/${auftragId}/status`, { status: zielStatus });
      await ladeAkte();
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setStatusFehler(err.response.data?.detail || 'Übergang nicht erlaubt.');
      } else {
        setStatusFehler('Status konnte nicht geändert werden.');
      }
    } finally {
      setStatusAktion(null);
    }
  };

  const handleRechnungErstellen = async () => {
    setRechnungFehler(null);
    setRechnungLaedt(true);
    try {
      await axios.post(`${API_URL}/api/dokumente/ausgangsrechnungen`, { auftrag_id: auftragId });
      await ladeAkte();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data?.detail ?? 'Rechnung konnte nicht erstellt werden.')
        : 'Rechnung konnte nicht erstellt werden.';
      setRechnungFehler(msg);
    } finally {
      setRechnungLaedt(false);
    }
  };

  /**
   * Beleg-PDF herunterladen. Ansehen bleibt der Beleg-Ansicht vorbehalten
   * (openAngebotAnsicht & Co.) — die zeigt denselben Beleg als Dokument,
   * ohne den Umweg über ein PDF.
   */
  const belegHerunterladen = async (
    typ: 'angebot' | 'auftragsbestaetigung' | 'lieferschein', kuerzel: string, bezeichnung: string,
  ) => {
    setDownloadFehler('');
    const fehler = await downloadDateiOderFehler(
      `${API_URL}/api/dokumente/auftraege/${auftragId}/${typ}/pdf`,
      `${kuerzel}_${nr4(auftragId)}.pdf`,
      bezeichnung,
    );
    if (fehler) setDownloadFehler(fehler);
  };

  const lieferscheinHerunterladen = () => belegHerunterladen('lieferschein', 'LS', 'Lieferschein');
  const angebotHerunterladen = () => belegHerunterladen('angebot', 'AN', 'Angebot');
  const auftragsbestaetigungHerunterladen = () =>
    belegHerunterladen('auftragsbestaetigung', 'AB', 'Auftragsbestätigung');

  const openAngebotAnsicht = () => {
    setBelegAnsicht({
      url: `${API_URL}/api/auftraege/${auftragId}/beleg-ansicht?typ=angebot`,
      title: `Angebot ${nr4(auftragId)}`,
    });
  };

  const openAuftragsbestaetigungAnsicht = () => {
    setBelegAnsicht({
      url: `${API_URL}/api/auftraege/${auftragId}/beleg-ansicht?typ=auftragsbestaetigung`,
      title: `Auftragsbestätigung ${nr4(auftragId)}`,
    });
  };

  const rechnungHerunterladen = async (r: AuftragsAkteRechnung) => {
    setDownloadFehler('');
    const fehler = await downloadDateiOderFehler(
      `${API_URL}/api/dokumente/ausgangsrechnungen/${r.ausgangsrechnung_id}/pdf`,
      `RE_${r.rechnungsnummer}.pdf`,
      'Rechnung',
    );
    if (fehler) setDownloadFehler(fehler);
  };

  if (loading) {
    return (
      <div className="aa-container">
        <div className="aa-loading">Auftragsakte wird geladen…</div>
      </div>
    );
  }

  if (error || !akte) {
    return (
      <div className="aa-container">
        <button className="aa-back-btn" onClick={onBack}>← Zurück</button>
        <div className="aa-error">{error || 'Auftragsakte nicht gefunden.'}</div>
      </div>
    );
  }

  const { auftrag } = akte;
  const stepStati = statusOptionen.filter((s) => s !== 'Storniert');
  const istStorniert = auftrag.status === 'Storniert';
  const currentIndex = stepStati.indexOf(auftrag.status);
  const naechsteSchritte = auftrag.naechste_schritte || [];

  const lieferstatusPositionen = akte.lieferstatus?.positionen || [];
  const lieferPct = lieferstatusPositionen.length
    ? Math.round(
        (lieferstatusPositionen.reduce((s, p) => s + Math.min(p.menge_geliefert, p.menge_bestellt), 0) /
          Math.max(
            1,
            lieferstatusPositionen.reduce((s, p) => s + p.menge_bestellt, 0)
          )) *
          100
      )
    : 0;

  return (
    <div className="aa-container">
      {/* Kopf */}
      <div className="aa-kopf">
        <button className="aa-back-btn" onClick={onBack}>← Zurück</button>
        <div className="aa-kopf-info">
          <h2 className="aa-auftrag-nr">{auftrag.auftrag_nr}</h2>
          <span className="aa-kunde">{auftrag.kunde?.name || '-'}</span>
          {auftrag.projekt_id != null && (
            <span className="aa-baustelle">Baustelle: {auftrag.projekt_bezeichnung || `#${auftrag.projekt_id}`}</span>
          )}
          {auftrag.quelle_regiebericht && (
            <button type="button" className="k-btn k-btn--ghost k-btn--sm"
                    title="Quell-Regiebericht öffnen"
                    onClick={() => setQuelleBerichtOffen(true)}>
              aus {auftrag.quelle_regiebericht.bericht_nr}
            </button>
          )}
          <span className={`aa-prio-badge aa-prio-${prioClass(auftrag.prioritaet)}`}>
            Priorität {auftrag.prioritaet}
          </span>
        </div>
        <div className="aa-kopf-summe">
          <span className="aa-summe-label">Gesamtsumme</span>
          <span className="aa-summe-wert">{formatPrice(auftrag.gesamtsumme)}</span>
        </div>
      </div>

      {/* Prozess-Stepper */}
      <div className="aa-stepper-section">
        {auftrag.auftrag_typ && (
          <div className="aa-typ-label">{TYP_LABEL[auftrag.auftrag_typ] || auftrag.auftrag_typ}</div>
        )}
        {stepStati.length > 0 && (
          <div className="aa-stepper">
            {stepStati.map((s, idx) => {
              let cls = 'aa-step';
              if (istStorniert) cls += ' aa-step--inactive';
              else if (idx < currentIndex) cls += ' aa-step--done';
              else if (idx === currentIndex) cls += ' aa-step--current';
              else cls += ' aa-step--upcoming';
              return (
                <React.Fragment key={s}>
                  <div className={cls}>
                    <span className="aa-step-dot">{idx < currentIndex && !istStorniert ? '✓' : idx + 1}</span>
                    <span className="aa-step-label">{s}</span>
                  </div>
                  {idx < stepStati.length - 1 && <div className="aa-step-connector" />}
                </React.Fragment>
              );
            })}
          </div>
        )}
        {istStorniert && <div className="aa-storniert-banner">Auftrag storniert</div>}

        {naechsteSchritte.length > 0 && (
          <div className="aa-aktionen">
            {naechsteSchritte.map((schritt) => (
              <button
                key={schritt}
                className={`aa-aktion-btn${schritt === 'Storniert' ? ' aa-aktion-btn--danger' : ''}`}
                disabled={statusAktion !== null}
                onClick={() => handleStatusChange(schritt)}
              >
                {statusAktion === schritt ? 'Wird gesetzt…' : `→ ${schritt}`}
              </button>
            ))}
          </div>
        )}
        {statusFehler && <div className="aa-inline-fehler">{statusFehler}</div>}
        {downloadFehler && <div className="aa-inline-fehler">{downloadFehler}</div>}
      </div>

      {/* Karten-Grid */}
      <div className="aa-grid">
        {/* Positionen */}
        <div className="aa-card">
          <h3 className="aa-card-title">Positionen</h3>
          {auftrag.positionen && auftrag.positionen.length > 0 ? (
            <table className="aa-positionen-table">
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th>Menge</th>
                  <th>Einzelpreis</th>
                  <th>Gesamt</th>
                </tr>
              </thead>
              <tbody>
                {auftrag.positionen.map((pos, idx) => (
                  <tr key={pos.position_id || idx}>
                    <td>
                      {pos.artikel?.bezeichnung || `Artikel #${pos.artikel_id}`}
                      {pos.regiebericht_nr && (
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-2)' }}>
                          aus {pos.regiebericht_nr}
                        </span>
                      )}
                    </td>
                    <td>{pos.menge}</td>
                    <td>{formatPrice(pos.preis_einzeln)}</td>
                    <td>{formatPrice(pos.preis_gesamt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="aa-empty">Keine Positionen</p>
          )}
          {auftrag.notizen && (
            <div className="aa-notizen">
              <strong>Notizen:</strong> {auftrag.notizen}
            </div>
          )}
        </div>

        {/* Regieberichte (AP 3.3) — Modul-Gating wie die Einsätze-Karte:
            der Bericht ist das Feldeinsatz-Artefakt (siehe Spec Abschnitt 8). */}
        {modulAktiv('einsatz') && (
          <RegieberichtKarte auftragId={auftragId} authToken={authToken} userRolle={userRolle} />
        )}

        {/* Nachkalkulation (AP 2.4) */}
        {kalkulation && (
          <div className="aa-card">
            <h3 className="aa-card-title">Nachkalkulation</h3>
            {kalkulation.hinweis && <p className="aa-empty">{kalkulation.hinweis}</p>}
            <div className="aa-kalk-summary">
              <div className="aa-kalk-item">
                <span className="aa-kalk-label">Soll</span>
                <span className="aa-kalk-value">{formatPrice(kalkulation.soll_gesamt)}</span>
              </div>
              <div className="aa-kalk-item">
                <span className="aa-kalk-label">Ist</span>
                <span className="aa-kalk-value">{formatPrice(kalkulation.ist_gesamt)}</span>
              </div>
              <div className="aa-kalk-item">
                <span className="aa-kalk-label">Deckungsbeitrag</span>
                <span
                  className={`aa-kalk-value aa-kalk-db aa-kalk-db--${deckungsbeitragAmpel(
                    kalkulation.deckungsbeitrag,
                    kalkulation.soll_gesamt
                  )}`}
                >
                  {formatPrice(kalkulation.deckungsbeitrag)}
                </span>
              </div>
            </div>
            <div className="aa-kalk-ist-detail">
              <span>Ist-Stunden: {kalkulation.ist_stunden.toFixed(2)} h ({formatPrice(kalkulation.ist_stunden_eur)})</span>
              <span>Ist-Material: {formatPrice(kalkulation.ist_material_eur)}</span>
            </div>
            {kalkulation.zeit_zeilen.length > 0 && (
              <>
                <button
                  className="aa-card-action aa-kalk-toggle"
                  onClick={() => setZeitDetailOffen((o) => !o)}
                >
                  {zeitDetailOffen ? 'Zeitbuchungen ausblenden' : 'Zeitbuchungen anzeigen'}
                </button>
                {zeitDetailOffen && (
                  <table className="aa-positionen-table aa-kalk-table">
                    <thead>
                      <tr>
                        <th>Datum</th>
                        <th>Mitarbeiter</th>
                        <th>Tätigkeit</th>
                        <th>Stunden</th>
                        <th>Betrag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kalkulation.zeit_zeilen.map((z) => (
                        <tr key={z.buchung_id}>
                          <td>{formatDate(z.datum)}</td>
                          <td>{z.mitarbeiter_name || '-'}</td>
                          <td>{z.taetigkeit || '-'}</td>
                          <td>{z.stunden.toFixed(2)} h</td>
                          <td>{z.betrag_eur !== null ? formatPrice(z.betrag_eur) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
            {kalkulation.material_detail.length > 0 && (
              <>
                <button
                  className="aa-card-action aa-kalk-toggle"
                  onClick={() => setMaterialDetailOffen((o) => !o)}
                >
                  {materialDetailOffen ? 'Material-Detail ausblenden' : 'Material-Detail anzeigen'}
                </button>
                {materialDetailOffen && (
                  <table className="aa-positionen-table aa-kalk-table">
                    <thead>
                      <tr>
                        <th>Artikel</th>
                        <th>Menge Soll</th>
                        <th>Menge Ist</th>
                        <th>EUR Soll</th>
                        <th>EUR Ist</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kalkulation.material_detail.map((z) => (
                        <tr key={z.artikel_id}>
                          <td>{z.bezeichnung}</td>
                          <td>{z.menge_soll} {z.einheit || ''}</td>
                          <td>{z.menge_ist} {z.einheit || ''}</td>
                          <td>{formatPrice(z.eur_soll)}</td>
                          <td>{formatPrice(z.eur_ist)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        )}

        {/* Dokumente (Angebot/AB) - statusunabhängig immer verfügbar */}
        <div className="aa-card">
          <div className="aa-card-header">
            <h3 className="aa-card-title">Dokumente</h3>
          </div>
          <table className="aa-belege-tabelle">
            <thead>
              <tr>
                <th>Beleg</th>
                <th>Ansicht</th>
                <th>Format</th>
              </tr>
            </thead>
            <tbody>
              <tr onClick={angebotHerunterladen} title="Angebot als PDF herunterladen">
                <td>Angebot</td>
                <td
                  className="aa-belege-ansicht"
                  onClick={(e) => { e.stopPropagation(); openAngebotAnsicht(); }}
                  title="Beleg-Ansicht öffnen"
                >
                  Ansicht
                </td>
                <td className="aa-belege-format">PDF</td>
              </tr>
              <tr onClick={auftragsbestaetigungHerunterladen} title="Auftragsbestätigung als PDF herunterladen">
                <td>Auftragsbestätigung</td>
                <td
                  className="aa-belege-ansicht"
                  onClick={(e) => { e.stopPropagation(); openAuftragsbestaetigungAnsicht(); }}
                  title="Beleg-Ansicht öffnen"
                >
                  Ansicht
                </td>
                <td className="aa-belege-format">PDF</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Lieferscheine */}
        {modulAktiv('lieferschein') && (
          <div className="aa-card">
            <div className="aa-card-header">
              <h3 className="aa-card-title">Lieferscheine</h3>
              <button className="aa-card-action" onClick={lieferscheinHerunterladen}>PDF herunterladen</button>
            </div>
            {lieferstatusPositionen.length > 0 && (
              <div className="aa-fortschritt">
                <div className="aa-progress-bar">
                  <div className="aa-progress-fill" style={{ width: `${lieferPct}%` }} />
                </div>
                <span className="aa-fortschritt-label">{lieferPct}% geliefert</span>
              </div>
            )}
            {akte.lieferscheine.length === 0 ? (
              <p className="aa-empty">Keine Lieferscheine</p>
            ) : (
              <ul className="aa-liste">
                {akte.lieferscheine.map((ls) => (
                  <li key={ls.lieferschein_id} className="aa-liste-item">
                    <span className="aa-liste-nr">{ls.lieferschein_nr}</span>
                    <span className={`aa-status-badge aa-status-badge--${statusKind(ls.status)}`}>{ls.status}</span>
                    <span className="aa-liste-datum">{formatDate(ls.erstellt_am)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Fertigungsaufträge */}
        {modulAktiv('fertigungsauftrag') && (
          <div className="aa-card">
            <h3 className="aa-card-title">Fertigungsaufträge</h3>
            {akte.fertigungsauftraege.length === 0 ? (
              <p className="aa-empty">Keine Fertigungsaufträge</p>
            ) : (
              <ul className="aa-liste">
                {akte.fertigungsauftraege.map((fa) => (
                  <li key={fa.fa_id} className="aa-liste-item">
                    <span className="aa-liste-nr">{fa.fa_nr}</span>
                    <span className={`aa-status-badge aa-status-badge--${statusKind(fa.status)}`}>{fa.status}</span>
                    <span className="aa-liste-datum">
                      {fa.fortschritt_prozent !== null ? `${fa.fortschritt_prozent}%` : '-'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Rechnungen */}
        {modulAktiv('zahlung') && (
          <div className="aa-card">
            <div className="aa-card-header">
              <h3 className="aa-card-title">Rechnungen</h3>
              <button className="aa-card-action" onClick={handleRechnungErstellen} disabled={rechnungLaedt}>
                {rechnungLaedt ? 'Erstellt…' : '+ Rechnung erstellen'}
              </button>
            </div>
            {rechnungFehler && <div className="aa-inline-fehler">{rechnungFehler}</div>}
            {akte.rechnungen.length === 0 ? (
              <p className="aa-empty">Keine Rechnungen</p>
            ) : (
              <ul className="aa-liste aa-liste--rechnungen">
                {akte.rechnungen.map((r) => (
                  <li key={r.ausgangsrechnung_id} className="aa-liste-item">
                    <span className="aa-liste-nr">{r.rechnungsnummer}</span>
                    <span className={`aa-status-badge aa-status-badge--${statusKind(r.status)}`}>{r.status}</span>
                    <span className="aa-liste-datum">{formatPrice(r.brutto_gesamt)}</span>
                    <span className={`aa-zahlung-badge${r.offen <= 0 ? ' aa-zahlung-badge--bezahlt' : ''}`}>
                      {r.offen <= 0 ? 'Bezahlt' : `${formatPrice(r.offen)} offen`}
                    </span>
                    <button className="aa-mini-btn" onClick={() => rechnungHerunterladen(r)}>PDF</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Retouren */}
        {modulAktiv('retoure') && (
          <div className="aa-card">
            <h3 className="aa-card-title">Retouren</h3>
            {akte.retouren.length === 0 ? (
              <p className="aa-empty">Keine Retouren</p>
            ) : (
              <ul className="aa-liste">
                {akte.retouren.map((ret) => (
                  <li key={ret.retoure_id} className="aa-liste-item">
                    <span className="aa-liste-nr">{ret.retoure_nr}</span>
                    <span className={`aa-status-badge aa-status-badge--${statusKind(ret.status)}`}>{ret.status}</span>
                    <span className="aa-liste-datum">{ret.grund || '-'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Sendungen */}
        {modulAktiv('sendung') && (
          <div className="aa-card">
            <h3 className="aa-card-title">Sendungen</h3>
            {akte.sendungen.length === 0 ? (
              <p className="aa-empty">Keine Sendungen</p>
            ) : (
              <ul className="aa-liste">
                {akte.sendungen.map((s) => (
                  <li key={s.sendung_id} className="aa-liste-item">
                    <span className="aa-liste-nr">{s.tracking_nr || `Sendung #${s.sendung_id}`}</span>
                    <span className={`aa-status-badge aa-status-badge--${statusKind(s.status)}`}>{s.status}</span>
                    <span className="aa-liste-datum">{s.spediteur || '-'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Einsätze */}
        {modulAktiv('einsatz') && (
          <div className="aa-card">
            <h3 className="aa-card-title">Einsätze</h3>
            {akte.einsaetze.length === 0 ? (
              <p className="aa-empty">Keine Einsätze</p>
            ) : (
              <ul className="aa-liste">
                {akte.einsaetze.map((ei) => (
                  <li key={ei.einsatz_id} className="aa-liste-item">
                    <span className="aa-liste-nr">{ei.mitarbeiter_name || `Einsatz #${ei.einsatz_id}`}</span>
                    <span className={`aa-status-badge aa-status-badge--${einsatzStatusKind(ei.status)}`}>{ei.status}</span>
                    <span className="aa-liste-datum">{formatDate(ei.beginn)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

      </div>

      <DokumenteKarte auftragId={auftragId} authToken={authToken} />

      {/* Notizen/Aufgaben */}
      <div className="aa-anhang-section">
        <NotizenAufgaben entitaetTyp="auftrag" entitaetId={auftragId} authToken={authToken} />
      </div>

      {/* Anhänge */}
      <div className="aa-anhang-section">
        <AnhangPanel entitaetTyp="auftrag" entitaetId={auftragId} authToken={authToken} />
      </div>

      {/* Timeline */}
      <div className="aa-timeline-section">
        <Timeline entitaetTyp="auftrag" entitaetId={auftragId} authToken={authToken} />
      </div>

      {belegAnsicht && (
        <BelegAnsichtModal
          url={belegAnsicht.url}
          title={belegAnsicht.title}
          kontext={{}}
          onClose={() => setBelegAnsicht(null)}
        />
      )}

      {quelleBerichtOffen && auftrag.quelle_regiebericht && (
        <RegieberichtForm
          {...(auftrag.quelle_regiebericht.auftrag_id != null
            ? { auftragId: auftrag.quelle_regiebericht.auftrag_id }
            : { projektId: auftrag.quelle_regiebericht.projekt_id as number })}
          regieberichtId={auftrag.quelle_regiebericht.id}
          authToken={authToken}
          userRolle={userRolle}
          onClose={() => setQuelleBerichtOffen(false)}
          onChanged={ladeAkte}
        />
      )}
    </div>
  );
};

export default AuftragsAkte;
